import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import './Dashboard.css';

// ── Pure SVG Donut Chart ─────────────────────────────────────────
function DonutChart({ segments, size = 200, strokeWidth = 28 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  let cumulativeOffset = 0;

  return (
    <div className="donut-chart-wrapper">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="donut-chart">
        {/* Background ring */}
        <circle
          cx={center} cy={center} r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={strokeWidth}
        />

        {total > 0 && segments.map((seg, i) => {
          const pct = seg.value / total;
          const dashLen = pct * circumference;
          const dashGap = circumference - dashLen;
          const offset = -cumulativeOffset * circumference;
          cumulativeOffset += pct;

          return (
            <circle
              key={i}
              cx={center} cy={center} r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dashLen} ${dashGap}`}
              strokeDashoffset={offset}
              strokeLinecap="round"
              transform={`rotate(-90 ${center} ${center})`}
              className="donut-segment"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          );
        })}
      </svg>

      {/* Center label */}
      <div className="donut-center">
        <span className="donut-center-value">{total}</span>
        <span className="donut-center-label">Total</span>
      </div>
    </div>
  );
}

// ── Pure SVG Bar Chart ───────────────────────────────────────────
function BarChart({ bars, maxHeight = 120 }) {
  const maxVal = Math.max(...bars.map(b => b.value), 1);

  return (
    <div className="bar-chart">
      {bars.map((bar, i) => {
        const heightPct = (bar.value / maxVal) * 100;
        return (
          <div key={i} className="bar-column">
            <div className="bar-value-label">{bar.value}</div>
            <div className="bar-track" style={{ height: maxHeight }}>
              <div
                className="bar-fill"
                style={{
                  height: `${heightPct}%`,
                  background: bar.color,
                  animationDelay: `${i * 0.12}s`,
                }}
              />
            </div>
            <div className="bar-label">{bar.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function Dashboard({ token, handleLogout }) {
  const [stats, setStats] = useState({ total: 0, completed: 0, in_progress: 0, todo: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        
        const [statsRes, historyRes] = await Promise.all([
          fetch(`${API_URL}/tasks/stats`, { headers }),
          fetch(`${API_URL}/tasks/history?limit=10`, { headers })
        ]);

        if (statsRes.status === 401 || historyRes.status === 401) {
          handleLogout();
          return;
        }

        const statsData = await statsRes.json();
        const historyData = await historyRes.json();

        setStats(statsData);
        setHistory(Array.isArray(historyData) ? historyData : []);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, handleLogout]);

  if (loading) return <div className="dashboard-loading">Loading Analytics...</div>;

  const completionPercent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  const chartSegments = [
    { label: 'To Do', value: stats.todo, color: '#0ea5e9' },
    { label: 'In Progress', value: stats.in_progress, color: '#f59e0b' },
    { label: 'Completed', value: stats.completed, color: '#22c55e' },
  ];

  const barData = [
    { label: 'To Do', value: stats.todo, color: 'linear-gradient(180deg, #0ea5e9, #0284c7)' },
    { label: 'In Progress', value: stats.in_progress, color: 'linear-gradient(180deg, #f59e0b, #d97706)' },
    { label: 'Completed', value: stats.completed, color: 'linear-gradient(180deg, #22c55e, #16a34a)' },
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Your Productivity Dashboard</h1>
        <p>Monitor your task progress and performance in real-time.</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon total-icon">📊</div>
          <h3>Total Tasks</h3>
          <div className="stat-value">{stats.total}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon todo-icon">📋</div>
          <h3>To Do</h3>
          <div className="stat-value">{stats.todo}</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon progress-icon">⚙️</div>
          <h3>In Progress</h3>
          <div className="stat-value">{stats.in_progress}</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon complete-icon">✅</div>
          <h3>Completed</h3>
          <div className="stat-value">{stats.completed}</div>
        </div>
      </div>

      {/* ── Charts Row ── */}
      <div className="charts-row">
        {/* Donut Chart */}
        <div className="chart-card">
          <h3>Task Distribution</h3>
          <DonutChart segments={chartSegments} size={180} strokeWidth={24} />
          <div className="chart-legend">
            {chartSegments.map((seg, i) => (
              <div key={i} className="legend-item">
                <span className="legend-dot" style={{ background: seg.color }} />
                <span className="legend-label">{seg.label}</span>
                <span className="legend-value">{seg.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bar Chart */}
        <div className="chart-card">
          <h3>Task Breakdown</h3>
          <BarChart bars={barData} maxHeight={140} />
        </div>
      </div>

      <div className="progress-section">
        <h3>Overall Completion Rate</h3>
        <div className="progress-bar-bg">
          <div className="progress-bar-fill" style={{ width: `${completionPercent}%` }}>
            {completionPercent}%
          </div>
        </div>
      </div>

      {/* ── Recent Activity Feed ── */}
      <div className="activity-feed">
        <div className="activity-feed-header">
          <h3>Recent Activity</h3>
          <span className="activity-feed-badge">{history.length} items</span>
        </div>
        
        {history.length === 0 ? (
          <div className="activity-empty">No recent activity found.</div>
        ) : (
          <div className="activity-timeline">
            {history.map((item, idx) => {
              let icon = '📝';
              let text = 'updated';
              if (item.action === 'created') { icon = '✨'; text = 'created'; }
              if (item.action === 'completed') { icon = '✅'; text = 'completed'; }
              if (item.action === 'deleted') { icon = '🗑️'; text = 'deleted'; }
              if (item.action === 'pomodoro_completed') { icon = '🍅'; text = 'completed a pomodoro for'; }

              const date = new Date(item.action_at).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
              });

              return (
                <div key={item.id} className="activity-item" style={{ animationDelay: `${idx * 0.08}s` }}>
                  <div className="activity-icon">{icon}</div>
                  <div className="activity-content">
                    <p>
                      You {text} <strong>{item.task_text}</strong>
                    </p>
                    <span className="activity-time">{date}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
